import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { useMemo, useState } from "react";
import type { JSX } from "react";

import type { SalesOrgLeadCard } from "@xop/dashboard-shared";

import { LEADS_COL_WIDTH, leadsTableMinWidth } from "./leads-table/columnWidths.js";
import { LeadRow } from "./leads-table/LeadRow.js";
import { defaultDir, sortLeads, type SortDir, type SortKey } from "./leads-table/sortLeads.js";
import { headCellSx } from "./leads-table/tableStyles.js";
import { Th } from "./leads-table/Th.js";

// Re-exported so `leads/leadSort.ts` keeps importing them from
// `../components/LeadsTable.js`.
export type { SortDir, SortKey } from "./leads-table/sortLeads.js";

interface LeadsTableProps {
  items: SalesOrgLeadCard[];
  /** Drops the Last-signal column AND the full-mode operator columns (Existing
   * Customer / RFP / Project Timeline / Project Budget / Lead score) to keep the
   * row narrow on the Today tab (which sits in a sidebar-ish width). The Leads
   * tab passes `compact={false}` to render the full table. The Created column
   * ("how long ago the lead was added") shows in BOTH modes — it's the headline
   * fact on the Today tab's "leads added today" list. */
  compact?: boolean;
  /** Whether to render the pipeline-Status pill column. Defaults to
   * `!compact` — full mode shows it, compact hides it. The **Today** tab opts
   * back in (`compact showStatus`) because its list now spans every status
   * (it passes `status=all` so archived leads created today stay visible), so
   * the operator needs the per-row stage marker (New / Archived / …) to tell
   * the agent's keeps from its discards. */
  showStatus?: boolean;
  /** Whether to render the **Quality** column — the lead-quality JUDGE's plain-
   * language verdict (Confirmed / Worth a look / Quality flag / Review pending),
   * the same `eval_verdict` the lead-detail hero badges. Off by default; the
   * **Today** tab opts in (`showQuality`) so the operator can see at a glance
   * how good each of today's finds is — including the archived ones. */
  showQuality?: boolean;
  /** When true, the **Status** pill and the **Vertical** badge become inline
   * editors: clicking the Status pill opens a menu to qualify the lead INTO the
   * CRM ("Qualify for CRM") or move it through the pipeline, and clicking the
   * Vertical badge opens a menu to re-classify it. Off by default (read-only).
   * BOTH the full **Leads** list and the **Today** "leads added today" glance
   * list opt in (`editable`), so a fresh find can be triaged straight from
   * Today. Every mutation goes through `salesApi` + invalidates the
   * `["sales","leads"]` query so every list (Leads + Today) refreshes. */
  editable?: boolean;
  /** Initial column ordering for the UNCONTROLLED mode (no `onSortChange`).
   * Defaults to heat-desc (the "most chase-worthy lead is row 1" design). Used
   * by surfaces that own no cross-page state and just re-sort the rows handed
   * to them. */
  defaultSort?: { key: SortKey; dir: SortDir };
  /** CONTROLLED mode (the Leads page). When BOTH are passed, the parent owns
   * the sort: header clicks call `onSortChange` instead of mutating local
   * state, so a click can drive a SERVER-side re-order of the whole dataset
   * (and a reset to page 1) rather than re-sorting only the visible page. The
   * table still applies `sort.key` within the page so the visible order mirrors
   * the server order. */
  sort?: { key: SortKey; dir: SortDir };
  onSortChange?: (next: { key: SortKey; dir: SortDir }) => void;
}

/**
 * Tabular renderer for SalesOrgLeadCard rows.
 *
 * This file is the **table shell** — the header row + the sorted body map. The
 * row, the cells/pills, the inline Status/Vertical editors, the sortable header,
 * and the pure sort logic live as focused modules under `leads-table/`:
 *
 *   - `LeadRow`     — one row (composes the cells + editors)
 *   - `cells`       — Heat/Confidence/ExistingCustomer/RFP/Placeholder/Quality/Status pills + `statusMeta`
 *   - `editors`     — the `editable` inline Status + Vertical menus
 *   - `Th`          — the sortable header cell
 *   - `sortLeads`   — `SortKey`/`SortDir` + the urgency-dominant sort (pure, injectable `now`)
 *   - `tableStyles` — the shared `cellSx` / `headCellSx`
 *   - `columnWidths` — the one width table + the `minWidth` floor that stops
 *                    Organisation (the only auto-width column) collapsing
 *
 * Designed for a CEO / sales lead scanning a long list: the heat pill leads
 * each row (urgency first), the Organisation cell is the only link (so screen
 * readers don't see a hundred ambiguous row targets), and headers flip
 * asc/desc. Default sort folds in deadline urgency so the most chase-worthy
 * lead is always row 1. See the module docs for each column's contract.
 */
export function LeadsTable({
  items,
  compact = false,
  showStatus = !compact,
  showQuality = false,
  editable = false,
  defaultSort,
  sort: sortProp,
  onSortChange,
}: LeadsTableProps): JSX.Element {
  // Controlled when the parent supplies BOTH `sort` and `onSortChange` (the
  // Leads page, which drives a server-side re-order). Otherwise the table owns
  // its own in-page sort state, seeded from `defaultSort`.
  const controlled = sortProp != null && onSortChange != null;
  const [internal, setInternal] = useState<{ key: SortKey; dir: SortDir }>(
    defaultSort ?? { key: "heat", dir: "desc" },
  );
  const sort = controlled ? sortProp : internal;

  const sorted = useMemo(() => sortLeads(items, sort.key, sort.dir), [items, sort]);

  // The placeholder operator columns (Project timeline / Project budget / Lead
  // score) have no data source yet. Rather than show three permanently-blank
  // columns, each is rendered ONLY when at least one lead in the list actually
  // carries that value — so the column appears automatically the moment the
  // agent starts emitting it, and stays out of the way until then.
  const cols = useMemo(
    () => ({
      timeline: items.some((l) => Boolean(l.project_timeline && l.project_timeline.trim())),
      budget: items.some((l) => Boolean(l.project_budget && l.project_budget.trim())),
      score: items.some((l) => l.lead_score != null),
    }),
    [items],
  );

  const onHeader = (key: SortKey) => {
    // Clicking the active column flips its direction; a new column starts at its
    // natural default (alpha → asc, urgency-like → desc).
    const next =
      sort.key === key
        ? { key, dir: (sort.dir === "asc" ? "desc" : "asc") as SortDir }
        : { key, dir: defaultDir(key) };
    if (controlled) onSortChange!(next);
    else setInternal(next);
  };

  return (
    <TableContainer
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t: Theme) => t.shadows[1],
        overflowX: "auto",
      }}
    >
      {/* `minWidth` is the floor that keeps Organisation — the only auto-width
        * column, and the only LINK in the row — from being crushed to 0px when
        * the fixed columns already outrun the viewport. It sums the widths of
        * the columns actually rendered plus `ORG_MIN_WIDTH`, so the container's
        * `overflowX: "auto"` scrolls instead. Above that width `width: "100%"`
        * still wins and Organisation absorbs all the slack. See
        * `leads-table/columnWidths.ts`. */}
      <Table
        sx={{
          width: "100%",
          minWidth: leadsTableMinWidth({ compact, showStatus, showQuality, cols }),
          tableLayout: "fixed",
          textAlign: "left",
        }}
        size="small"
      >
        <TableHead>
          <TableRow sx={{ borderBottom: 1, borderColor: "divider" }}>
            {/* Wide enough for the longest urgency-elevated pill ("LAST CHANCE")
              * on one line — a closing lead's heat reads SUPER HOT / LAST CHANCE,
              * not just a 4-letter qualitative heat. */}
            <Th sort={sort} colKey="heat" onClick={onHeader} width={LEADS_COL_WIDTH.heat}>
              Heat
            </Th>
            <Th sort={sort} colKey="name" onClick={onHeader}>
              Organisation
            </Th>
            <Th sort={sort} colKey="country" onClick={onHeader} width={LEADS_COL_WIDTH.country}>
              Country
            </Th>
            {/* Provenance as a first-class column (2026-06-21) — which AI agent
              * produced this lead (Lead Generator vs Harvester). Sortable so the
              * operator can group one bot's leads together. */}
            <Th sort={sort} colKey="source" onClick={onHeader} width={LEADS_COL_WIDTH.source}>
              Found by
            </Th>
            {/* Wide enough for the longest single-line vertical pill
              * ("HOSPITALS / HEALTHCARE", 22 uppercase chars) plus the inline
              * re-classify ▾ arrow — the table is `tableLayout: "fixed"`, so a
              * too-narrow column would either wrap the badge or spill into the
              * next column. The badge itself never wraps (`VerticalBadge` baseSx
              * `whiteSpace: "nowrap"`).
              *
              * ⚠ Treat 22 chars as a LABEL BUDGET, not this width as adjustable.
              * Organisation is the only column with NO fixed width, so it
              * absorbs whatever the fixed ones leave — and they already sum to
              * ~1200 of the ~1152px the shell hands the table at a 1280px
              * viewport. Since 2026-08-31 that no longer collapses the org-name
              * link to 0px (the table carries a computed `minWidth` and the
              * container scrolls — see `columnWidths.ts`), but widening this
              * column still costs every operator horizontal scrolling on a
              * laptop. The 2026-08-30 airport / military-base split first
              * shipped 29-30 char labels and widened this to 260 to fit them;
              * the labels were shortened back inside 22 chars instead — do that
              * for a new vertical too. */}
            <Th sort={sort} colKey="vertical" onClick={onHeader} width={LEADS_COL_WIDTH.vertical}>
              Vertical
            </Th>
            {/* Operator columns (full mode only, right after Vertical). The
              * Bundle column was dropped here 2026-06-16 — it just echoed every
              * product and conveyed nothing. Existing Customer + RFP are derived
              * from fields the card already carries, so they always show; Project
              * Timeline / Budget / Lead score have no data source yet, so each is
              * rendered only when some lead carries it (`cols.*`). */}
            {!compact && (
              <>
                <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.existingCustomer }]}>Existing customer</TableCell>
                <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.rfp }]}>RFP</TableCell>
                {cols.timeline && (
                  <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.timeline }]}>Project timeline</TableCell>
                )}
                {cols.budget && (
                  <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.budget }]}>Project budget</TableCell>
                )}
                {cols.score && (
                  <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.score }]}>Lead score</TableCell>
                )}
              </>
            )}
            {/* Quality (the judge's verdict) supersedes the agent's raw
              * Confidence% on the Today glance list — a richer "how good is it"
              * signal in the same slot. Confidence stays on the full Leads tab
              * + lead detail. */}
            {!showQuality && (
              <Th sort={sort} colKey="confidence" onClick={onHeader} width={LEADS_COL_WIDTH.confidence}>
                Confidence
              </Th>
            )}
            <Th sort={sort} colKey="created" onClick={onHeader} width={LEADS_COL_WIDTH.created}>
              Created
            </Th>
            {!compact && (
              <Th sort={sort} colKey="last" onClick={onHeader} width={LEADS_COL_WIDTH.last}>
                Last signal
              </Th>
            )}
            {showQuality && (
              <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.quality }]}>Quality</TableCell>
            )}
            {showStatus && (
              <TableCell sx={[headCellSx, { width: LEADS_COL_WIDTH.status, textAlign: "right" }]}>Status</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((l) => (
            <LeadRow
              key={l.id}
              lead={l}
              compact={compact}
              showStatus={showStatus}
              showQuality={showQuality}
              editable={editable}
              cols={cols}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
